import { onSnapshot, query, where, collection, doc, updateDoc, orderBy, limit, getDocs, Timestamp, type FirestoreError } from 'firebase/firestore';
import { db } from '@/firebase';
import { logAnalyticsEvent, ANALYTICS_EVENTS, type AnalyticsEventType } from '@/utils/Analytics';
import { logger } from '@/utils/logger';

interface AnalyticsNotification {
  id: string;
  userId: string;
  eventName: string;
  eventParams: Record<string, unknown>;
  source: string;
  processed: boolean;
  createdAt: Timestamp | Date | null;
  expiresAt: Timestamp | Date | null;
}

/**
 * Backend event names the SERVER already reports to GA4 itself, so the browser
 * must NOT re-send them or the event is counted twice (GA4 only dedupes
 * `purchase` by transaction_id — custom events have no dedupe key):
 *
 *  - `subscription_completed` → the browser fires it on the success page
 *    (PaymentSuccessRedirect → trackGoogleAdsConversion), and the revenue
 *    itself lands as `purchase` via the Measurement Protocol
 *    (aswb-backend handleInvoicePaid, transaction_id = Stripe invoice id).
 *  - `subscription_cancelled` → sent server-side via the Measurement Protocol
 *    from cancelUserSubscription (customer.subscription.deleted), while the
 *    notification doc is written from customer.subscription.updated — the SAME
 *    cancellation reaches GA4 twice if the browser also forwards it.
 *
 * These notifications are still consumed (marked processed) so they don't
 * accumulate and re-fire on every reload; they are just not re-emitted.
 */
const SERVER_SENT_EVENT_NAMES = new Set<string>([
  'subscription_completed',
  'subscription_cancelled',
]);

/** Matches the backend's `expiresAt` (24h). Older docs are stale: a GA4 event
 *  fired now would be timestamped now and land in the wrong day/session. */
const MAX_NOTIFICATION_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Backend Analytics Listener Service
 *
 * Listens for analytics notifications from the backend (`analyticsNotifications`,
 * written by aswb-backend's AnalyticsNotifier on Stripe webhooks) and forwards
 * the ones the server does NOT already report to GA4 — today that is the payment
 * failure family (`subscription_failed`/`payment_failed`) plus backend errors.
 *
 * Started from useFirebaseAuthState when auth resolves with a user, stopped on sign
 * out (useAuthActions.logOut) and when auth resolves to null.
 */
export class BackendAnalyticsListener {
  private unsubscribe: (() => void) | null = null;
  private userId: string | null = null;
  /** Doc ids already handled in this page session — makes processing idempotent
   *  even if a snapshot re-delivers a doc or processPendingNotifications races
   *  the live listener. */
  private handled = new Set<string>();

  /** The uid currently being listened to, or null when stopped (for tests/diagnostics). */
  get listeningUserId(): string | null {
    return this.unsubscribe ? this.userId : null;
  }

  /**
   * Start listening for analytics notifications for the current user.
   *
   * The first onSnapshot delivery contains every unprocessed notification as an
   * `added` change, so pending notifications that arrived while the app was
   * closed are processed on start — no separate catch-up pass required.
   */
  startListening(userId: string): void {
    if (!userId) return;
    // Already listening for this user: re-subscribing would replay the whole
    // pending set through a fresh initial snapshot.
    if (this.unsubscribe && this.userId === userId) return;

    this.stopListening(); // Clean up any existing listener
    this.userId = userId;

    try {
      // Listen for unprocessed analytics notifications for this user
      const notificationsQuery = query(
        collection(db, 'analyticsNotifications'),
        where('userId', '==', userId),
        where('processed', '==', false),
        orderBy('createdAt', 'desc'),
        limit(50) // Process up to 50 pending notifications
      );

      this.unsubscribe = onSnapshot(
        notificationsQuery,
        (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            if (change.type === 'added') {
              const notification = {
                id: change.doc.id,
                ...change.doc.data()
              } as AnalyticsNotification;

              void this.processNotification(notification);
            }
          });
        },
        (error: FirestoreError) => {
          // `permission-denied` means the Firestore rules don't grant the user
          // read access to their own analyticsNotifications — a backend config
          // gap, not a runtime bug. Warn (don't error) so it can't flood Rollbar
          // on every sign-in.
          if (error?.code === 'permission-denied') {
            logger.warn('BackendAnalyticsListener', 'Notifications not readable (rules)', { code: error.code });
            this.stopListening();
            return;
          }
          logger.error('BackendAnalyticsListener', 'Error listening for notifications', { error });
        }
      );

      logger.debug('BackendAnalyticsListener', 'Started listening for analytics notifications', { userId });

    } catch (error) {
      logger.error('BackendAnalyticsListener', 'Failed to start listening', { error, userId });
    }
  }

  /**
   * Stop listening for analytics notifications
   */
  stopListening(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
      logger.debug('BackendAnalyticsListener', 'Stopped listening for analytics notifications');
    }
    this.userId = null;
    this.handled.clear();
  }

  /**
   * Process a single analytics notification by sending it to GA4
   */
  private async processNotification(notification: AnalyticsNotification): Promise<void> {
    // Idempotency: never emit the same notification doc twice per session.
    if (this.handled.has(notification.id)) return;
    this.handled.add(notification.id);

    try {
      // Events the server already reports to GA4: consume, never re-emit.
      if (SERVER_SENT_EVENT_NAMES.has(notification.eventName)) {
        logger.debug('BackendAnalyticsListener', 'Skipping server-sent event (already in GA4)', {
          eventName: notification.eventName,
          notificationId: notification.id
        });
        await this.markAsProcessed(notification.id);
        return;
      }

      // Map backend event names to frontend event constants
      const eventName = this.mapEventName(notification.eventName);

      if (!eventName) {
        logger.warn('BackendAnalyticsListener', 'Unknown event name from backend', { 
          eventName: notification.eventName 
        });
        await this.markAsProcessed(notification.id);
        return;
      }

      const createdAtMs = this.toMillis(notification.createdAt);
      const ageMs = createdAtMs === null ? 0 : Date.now() - createdAtMs;
      if (ageMs > MAX_NOTIFICATION_AGE_MS) {
        logger.warn('BackendAnalyticsListener', 'Dropping stale notification', {
          notificationId: notification.id,
          eventName: notification.eventName,
          ageMs
        });
        await this.markAsProcessed(notification.id);
        return;
      }

      // Send to GA4 using existing frontend integration
      await logAnalyticsEvent(eventName, {
        ...notification.eventParams,
        backend_triggered: true,
        notification_id: notification.id,
        processing_delay: ageMs
      } as Parameters<typeof logAnalyticsEvent>[1]);

      // Mark as processed
      await this.markAsProcessed(notification.id);

      logger.debug('BackendAnalyticsListener', 'Processed analytics notification', {
        eventName: notification.eventName,
        notificationId: notification.id
      });

    } catch (error) {
      logger.error('BackendAnalyticsListener', 'Failed to process notification', {
        error,
        notificationId: notification.id,
        eventName: notification.eventName
      });

      // Don't mark as processed if failed - allow a retry on the next app load
      this.handled.delete(notification.id);
    }
  }

  private toMillis(value: Timestamp | Date | null): number | null {
    if (value instanceof Timestamp) return value.toDate().getTime();
    if (value instanceof Date) return value.getTime();
    return null;
  }

  /**
   * Map backend event names to frontend ANALYTICS_EVENTS constants.
   * Only events the SERVER does not already send (see SERVER_SENT_EVENT_NAMES).
   */
  private mapEventName(backendEventName: string): AnalyticsEventType | null {
    const eventMap: Record<string, AnalyticsEventType> = {
      'subscription_failed': ANALYTICS_EVENTS.SUBSCRIPTION_FAILED,
      'payment_failed': ANALYTICS_EVENTS.SUBSCRIPTION_FAILED, // Map to subscription_failed
      'backend_error': ANALYTICS_EVENTS.ERROR_OCCURRED,
      'quiz_completed_backend': ANALYTICS_EVENTS.QUIZ_COMPLETED,
      'adaptive_decision': ANALYTICS_EVENTS.ADAPTIVE_ALGORITHM_DECISION,
    };

    return eventMap[backendEventName] || null;
  }

  /**
   * Mark a notification as processed
   */
  private async markAsProcessed(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'analyticsNotifications', notificationId);
      await updateDoc(notificationRef, {
        processed: true,
        processedAt: new Date(),
        processedBy: 'frontend'
      });
    } catch (error) {
      logger.error('BackendAnalyticsListener', 'Failed to mark notification as processed', {
        error,
        notificationId
      });
    }
  }

  /**
   * Process any pending notifications for the current user.
   * The live listener's initial snapshot already covers this; kept for explicit
   * catch-up calls and safe to run alongside it (processing is idempotent).
   */
  async processPendingNotifications(userId: string): Promise<void> {
    try {
      const notificationsQuery = query(
        collection(db, 'analyticsNotifications'),
        where('userId', '==', userId),
        where('processed', '==', false),
        orderBy('createdAt', 'asc') // Process oldest first
      );

      const snapshot = await getDocs(notificationsQuery);
      
      for (const docSnapshot of snapshot.docs) {
        const notification = {
          id: docSnapshot.id,
          ...docSnapshot.data()
        } as AnalyticsNotification;

        await this.processNotification(notification);
      }

      logger.debug('BackendAnalyticsListener', 'Processed pending notifications', {
        count: snapshot.size,
        userId
      });

    } catch (error) {
      logger.error('BackendAnalyticsListener', 'Failed to process pending notifications', {
        error,
        userId
      });
    }
  }
}

// Export singleton instance
export const backendAnalyticsListener = new BackendAnalyticsListener();
